import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, ChevronDown, Star, Clock } from "lucide-react";
import { api, money } from "@/lib/api";

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("pt-BR") : "");
const MSTATUS = { concluido: "Concluído", confirmado: "Confirmado", cancelado: "Cancelado" };

export default function ExpressHistory() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/express-history").then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, []);
  if (!data || !data.items.length) return null;
  const t = data.totals;
  return (
    <section aria-labelledby="express-history-title" data-testid="express-history" className="rounded-2xl border border-white/10 bg-[#121217] p-5 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 id="express-history-title" className="font-head text-lg font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-[#FF4D00]" aria-hidden="true" />Chamadas ⚡ encerradas</h2>
        <dl className="flex gap-4 text-xs text-zinc-300" data-testid="express-history-totals">
          <div><dt className="inline">Concluídas: </dt><dd className="inline font-semibold text-white">{t.concluded}</dd></div>
          <div><dt className="inline">Canceladas: </dt><dd className="inline font-semibold text-white">{t.cancelled}</dd></div>
          <div><dt className="inline">Cachês: </dt><dd className="inline font-semibold text-[#FF8A3D]">{money(t.total_cache)}</dd></div>
          {t.avg_rating && <div><dt className="inline">Nota média: </dt><dd className="inline font-semibold text-white">{t.avg_rating}</dd></div>}
        </dl>
      </div>
      <ul className="space-y-2">{data.items.map((c) => <HistoryItem key={c.id} c={c} />)}</ul>
    </section>
  );
}

function HistoryItem({ c }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const id = `xh-${c.id}`;
  return (
    <li className="rounded-xl border border-white/10 bg-black/20" data-testid={`express-history-${c.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls={id}
        className="w-full min-h-[56px] px-4 py-3 flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D] rounded-xl">
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">⚡ {c.name.replace("⚡ Em cima da Hora — ", "")}</div>
          <div className="text-xs text-zinc-400">{fmtDate(c.date)} · {c.start_time}–{c.end_time} · {c.musicians.length} músico(s){c.turbo_total ? ` · Turbo +${money(c.turbo_total)}` : ""}</div>
        </div>
        <span className={`sm:order-none order-last basis-full sm:basis-auto w-fit text-xs font-semibold px-2 py-1 rounded-full ${c.status === "cancelado" ? "bg-rose-500/15 text-rose-300" : c.musicians.length ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-500/15 text-zinc-300"}`}>{c.status === "cancelado" ? "Cancelada" : c.musicians.length ? "Concluída" : "Encerrada sem contratação"}</span>
        <span className="text-sm font-semibold text-[#FF8A3D] hidden sm:block">{money(c.total_cache)}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div id={id} className="px-4 pb-4 space-y-3">
          {c.cancel_reason && <p className="text-xs text-rose-200">Motivo do cancelamento da chamada: {c.cancel_reason}</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <caption className="sr-only">Músicos da chamada {c.name}</caption>
              <thead><tr className="text-xs text-zinc-400 text-left"><th className="py-1.5 font-medium">Músico</th><th className="font-medium">Cachê</th><th className="font-medium">Chegada</th><th className="font-medium">Saída</th><th className="font-medium">Avaliação</th><th className="font-medium">Status</th></tr></thead>
              <tbody>
                {c.musicians.map((m) => (
                  <tr key={m.application_id} className="border-t border-white/5" data-testid={`express-history-row-${m.application_id}`}>
                    <td className="py-2"><div className="font-medium">{m.stage_name}</div><div className="text-xs text-zinc-400">{m.instrument}</div></td>
                    <td>{money(m.cache)}</td>
                    <td><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-zinc-400" aria-hidden="true" />{hhmm(m.checkin_at)}</span>
                      {m.checkin_at && <div className="text-xs text-zinc-400">{m.checkin_status}{m.checkin_method === "creator_confirmed" ? " · confirmada por você" : " · GPS"}</div>}</td>
                    <td>{hhmm(m.checkout_at)}</td>
                    <td>{m.rating_given ? <span className="inline-flex items-center gap-0.5"><Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" aria-hidden="true" />{m.rating_given}<span className="sr-only"> de 5</span></span> : <span className="text-zinc-500">—</span>}</td>
                    <td className="text-xs">{MSTATUS[m.status] || m.status}{m.cancelled_by && <div className="text-rose-300">por {m.cancelled_by === "creator" ? "você" : "músico"}{m.cancel_reason ? `: ${m.cancel_reason}` : ""}</div>}</td>
                  </tr>
                ))}
                {!c.musicians.length && <tr><td colSpan={6} className="py-2 text-zinc-400 text-xs">Nenhum músico foi contratado nesta chamada.</td></tr>}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={() => navigate(`/eventos/${c.id}`)} className="text-xs text-[#FF8A3D] underline min-h-[44px]" data-testid={`express-history-open-${c.id}`}>Ver detalhes do evento (somente leitura)</button>
        </div>
      )}
    </li>
  );
}
